import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import edgeMonitorApi from "../edgeMonitorApi";
import { GatewayMachineSummary } from "../models/edgeMonitorModels";

/**
 * Machine picker for the EDGE-LIVE-MONITOR Zone 1 header.
 *
 * Drives a list of `GET /v1/gateway/machines` (commit b8a610da) into a
 * keyboard-first dropdown. Selecting a machine pushes the GUID into the URL
 * `?machineId=<guid>` via react-router; the page already reads `machineId`
 * from query params and re-renders against the new machine.
 *
 * Three visual states match the Paper artboards 4SG-0 (empty / hero), 4UN-0
 * (closed) and 4XY-0 (open) in the `Machine Connections` Paper file. Styles
 * are translated from `mcp__paper__get_jsx` exports — do NOT eyeball.
 */

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";
const FONT_INTER = "'Inter', system-ui, -apple-system, sans-serif";

const COLORS = {
  charcoal: "#1A1F25",
  charcoalLight: "#FAFAF7",
  primaryText: "#1A1F25",
  bodyText: "#6B7280",
  dimText: "#8A857B",
  veryDim: "#B0AA9D",
  pageBg: "#FAFAF7",
  white: "#FFFFFF",
  inputBg: "#FAFAF7",
  border: "#EFEAE0",
  rowDivider: "#F4F0E6",
  green: "#2D8C4E",
  greenBg: "#E8EFE6",
  amber: "#D4B040",
  amberText: "#A8861E",
  amberBgChip: "#FBF5E2",
  amberBorderChip: "#ECD895",
  grey: "#8C9099",
  greyChipBg: "#F2F0EB",
  greyChipBorder: "#DDD8CD",
  veryDimRow: "#A8A39A",
  ringGreen: "#2D8C4E29",
  kbdBorder: "#DDD8CD",
};

const STATUS_FRESH_THRESHOLD_MS = 60_000; // online + lastDataReceived <60s = fresh
const STATUS_STALE_THRESHOLD_MS = 60 * 60 * 1000; // online but lastDataReceived >60s up to 1h = stale-flag

export interface MachinePickerProps {
  /**
   * The GUID of the currently-loaded machine (from the URL `?machineId=`).
   * Drives the "Current" chip + sage row tint in the open dropdown, and the
   * label text + status-dot color in the closed state. `null` => empty hero
   * state (artboard 4SG-0).
   */
  currentMachineId: string | null;
  /**
   * Display name of the loaded machine (resolved by the page from
   * `IMachineryClient.GetMachineAsync`). Used as the closed-state label.
   */
  currentMachineName: string | null;
  /**
   * Visual mode:
   *   - "hero" (default empty state) — wide field with hero typography below
   *   - "compact" (closed inside Zone 1) — smaller chrome
   */
  variant?: "hero" | "compact";
}

interface RowMeta {
  freshness: "fresh" | "stale" | "offline";
  agoLabel: string;
  agoColor: string;
  statusLabel: string;
}

/**
 * Compute freshness + ago-label + status from a `GatewayMachineSummary`.
 * Matches the artboard rules: green dot+bold = online & lastData<60s,
 * amber dot+STALE = online but lastData>60s, grey dot+offline.
 */
const deriveRowMeta = (m: GatewayMachineSummary, now: number): RowMeta => {
  const lastDataMs = m.lastDataReceived ? Date.parse(m.lastDataReceived) : NaN;
  const ageMs = Number.isNaN(lastDataMs) ? Number.POSITIVE_INFINITY : now - lastDataMs;
  let freshness: RowMeta["freshness"];
  if (!m.agentOnline) {
    freshness = "offline";
  } else if (ageMs < STATUS_FRESH_THRESHOLD_MS) {
    freshness = "fresh";
  } else {
    freshness = "stale";
  }

  let agoLabel = "—";
  if (Number.isFinite(ageMs)) {
    const s = Math.floor(ageMs / 1000);
    if (s < 60) agoLabel = `${s}s ago`;
    else if (s < 3600) agoLabel = `${Math.floor(s / 60)} min ago`;
    else if (s < 86_400) agoLabel = `${Math.floor(s / 3600)}h ago`;
    else agoLabel = `${Math.floor(s / 86_400)}d ago`;
  }

  let agoColor = COLORS.primaryText;
  if (freshness === "stale") agoColor = COLORS.amberText;
  else if (freshness === "offline") agoColor = COLORS.grey;

  const statusLabel = m.status ?? (freshness === "offline" ? "Offline" : "—");

  return { freshness, agoLabel, agoColor, statusLabel };
};

const dotColorForRow = (freshness: RowMeta["freshness"]): string => {
  if (freshness === "fresh") return COLORS.green;
  if (freshness === "stale") return COLORS.amber;
  return COLORS.grey;
};

const MagnifierIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = COLORS.charcoal,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <circle cx="7" cy="7" r="4.5" stroke={color} strokeWidth="1.5" />
    <path d="M10.5 10.5l3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CaretDownIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M5 7l4 4 4-4"
      stroke={COLORS.charcoal}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortIcon: React.FC = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M3 4l2-2 2 2M3 6l2 2 2-2"
      stroke={COLORS.charcoal}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const KbdChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      alignItems: "center",
      backgroundColor: COLORS.white,
      borderColor: COLORS.kbdBorder,
      borderRadius: "4px",
      borderStyle: "solid",
      borderWidth: "1px",
      boxSizing: "border-box",
      display: "flex",
      flexShrink: 0,
      padding: "1px 6px",
      fontFamily: FONT_MONO,
      fontSize: "9px",
      fontWeight: 600,
      letterSpacing: "0.06em",
      color: COLORS.bodyText,
      lineHeight: "12px",
    }}
  >
    {children}
  </div>
);

const MachinePicker: React.FC<MachinePickerProps> = ({
  currentMachineId,
  currentMachineName,
  variant = "hero",
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [machines, setMachines] = useState<GatewayMachineSummary[] | null>(null);
  const [search, setSearch] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [now, setNow] = useState<number>(() => Date.now());

  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const rowsRef = useRef<Array<HTMLDivElement | null>>([]);

  // Tick clock once per second so "Ns ago" labels stay live while the dropdown
  // is open. No network — just re-renders.
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // Load machines whenever the dropdown opens. Cheap; the endpoint returns
  // <100 rows on the largest installs.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    edgeMonitorApi.listMachines().then((data) => {
      if (cancelled) return;
      setMachines(data);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Filter rows by case-insensitive substring match on `name`.
  const filtered = useMemo<GatewayMachineSummary[]>(() => {
    if (!machines) return [];
    const q = search.trim().toLowerCase();
    if (!q) return machines;
    return machines.filter((m) => m.name.toLowerCase().includes(q));
  }, [machines, search]);

  // Reset highlight when filter changes.
  useEffect(() => {
    setHighlightIdx(0);
  }, [search, machines]);

  // Keep highlighted row scrolled into view.
  useEffect(() => {
    if (!open) return;
    const el = rowsRef.current[highlightIdx];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlightIdx, open]);

  // Focus search input on open.
  useLayoutEffect(() => {
    if (open) {
      // Defer so the input is mounted.
      const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  const closePicker = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  const selectMachine = useCallback(
    (m: GatewayMachineSummary) => {
      // Push GUID into URL via react-router. Page reads `?machineId=` from
      // useSearchParams() — single source of truth.
      navigate({ pathname: "/internal/tools/edge-monitor", search: `?machineId=${m.machineId}` });
      closePicker();
    },
    [navigate, closePicker]
  );

  // Outside-click closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        closePicker();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, closePicker]);

  // Global keyboard listeners.
  //   - ⌘K / Ctrl+K  — opens picker (or focuses search if already open)
  //   - Esc          — closes (only when open, registered conditionally below)
  //   - ↑ ↓ ↵        — navigation/select, scoped to this component when open
  // ⌘K is registered globally and unconditionally so it works while the
  // picker is closed; the open-only listener handles the in-dropdown nav.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isCmdK =
        (e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey);
      if (isCmdK) {
        e.preventDefault();
        setOpen(true);
        // searchInputRef focuses via the open-effect's useLayoutEffect.
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Per-state nav listener. We register/unregister with `open` so the doc
  // doesn't field arrow keys while the picker is closed.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePicker();
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(filtered.length - 1, i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Enter") {
        const target = filtered[highlightIdx];
        if (target) {
          e.preventDefault();
          selectMachine(target);
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, filtered, highlightIdx, closePicker, selectMachine]);

  // ───── Closed-state trigger ─────
  const dotColorForCurrent = (() => {
    if (!currentMachineId) return COLORS.grey;
    const match = machines?.find((m) => m.machineId === currentMachineId);
    if (!match) return COLORS.green; // unknown — default optimistic
    const meta = deriveRowMeta(match, now);
    return dotColorForRow(meta.freshness);
  })();

  const triggerStyle: React.CSSProperties =
    variant === "hero"
      ? {
          alignItems: "center",
          backgroundColor: COLORS.inputBg,
          borderColor: COLORS.charcoal,
          borderRadius: "10px",
          borderStyle: "solid",
          borderWidth: "1.5px",
          boxShadow: "rgba(26, 31, 37, 0.03) 0px 1px 0px, rgba(26, 31, 37, 0.05) 0px 8px 24px",
          boxSizing: "border-box",
          cursor: "pointer",
          display: "flex",
          gap: "14px",
          padding: "18px 22px",
          textAlign: "left",
          width: "100%",
          minWidth: 0,
          appearance: "none" as const,
          font: "inherit",
          color: COLORS.primaryText,
        }
      : {
          alignItems: "center",
          backgroundColor: COLORS.inputBg,
          borderColor: COLORS.charcoal,
          borderRadius: "8px",
          borderStyle: "solid",
          borderWidth: "1.5px",
          boxSizing: "border-box",
          cursor: "pointer",
          display: "flex",
          gap: "10px",
          padding: "6px 12px 6px 14px",
          textAlign: "left",
          appearance: "none" as const,
          font: "inherit",
          color: COLORS.primaryText,
        };

  const heroLabel = currentMachineName ?? "Select a machine to monitor";
  const closedLabel = currentMachineName ?? "Select machine";

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", width: variant === "hero" ? "100%" : "auto" }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          variant === "hero"
            ? "Select a machine to monitor"
            : `Current machine: ${closedLabel}. Click to switch.`
        }
        onClick={() => setOpen((o) => !o)}
        style={triggerStyle}
      >
        {variant === "hero" ? (
          <>
            <span
              style={{
                alignItems: "center",
                display: "flex",
                flexShrink: 0,
                height: "18px",
                justifyContent: "center",
                width: "18px",
              }}
            >
              <MagnifierIcon size={18} />
            </span>
            <span
              style={{
                color: COLORS.primaryText,
                flexGrow: 1,
                fontFamily: FONT_SORA,
                fontSize: "17px",
                fontWeight: 500,
                letterSpacing: "-0.01em",
                lineHeight: "22px",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {heroLabel}
            </span>
            <span
              style={{
                alignItems: "center",
                backgroundColor: COLORS.charcoal,
                borderRadius: "6px",
                display: "flex",
                flexShrink: 0,
                gap: "6px",
                padding: "5px 9px",
              }}
            >
              <span
                style={{
                  color: COLORS.charcoalLight,
                  fontFamily: FONT_MONO,
                  fontSize: "10px",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                }}
              >
                {/Mac/i.test(navigator.platform) ? "⌘K" : "Ctrl K"}
              </span>
            </span>
            <CaretDownIcon size={18} />
          </>
        ) : (
          <>
            <span
              aria-hidden="true"
              style={{
                backgroundColor: dotColorForCurrent,
                borderRadius: "50%",
                boxShadow: dotColorForCurrent === COLORS.green ? `0 0 0 3px ${COLORS.ringGreen}` : undefined,
                flexShrink: 0,
                height: "8px",
                width: "8px",
              }}
            />
            <span
              style={{
                color: COLORS.primaryText,
                fontFamily: FONT_SORA,
                fontSize: "22px",
                fontWeight: 600,
                letterSpacing: "-0.015em",
                lineHeight: "30px",
                maxWidth: "320px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {closedLabel}
            </span>
            <CaretDownIcon size={14} />
          </>
        )}
      </button>

      {open && (
        <div
          style={{
            backgroundColor: COLORS.white,
            borderColor: COLORS.charcoal,
            borderRadius: "10px",
            borderStyle: "solid",
            borderWidth: "1.5px",
            boxShadow:
              "rgba(26, 31, 37, 0.03) 0px 1px 0px, rgba(26, 31, 37, 0.15) 0px 16px 48px, rgba(26, 31, 37, 0.10) 0px 4px 12px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            left: 0,
            marginTop: variant === "hero" ? "6px" : "8px",
            overflow: "hidden",
            position: "absolute",
            top: "100%",
            width: "540px",
            maxWidth: variant === "hero" ? "calc(100vw - 64px)" : undefined,
            zIndex: 30,
          }}
        >
          {/* Search header */}
          <div
            style={{
              alignItems: "center",
              backgroundColor: COLORS.inputBg,
              borderBottomColor: COLORS.border,
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              boxSizing: "border-box",
              display: "flex",
              gap: "14px",
              padding: "14px 18px",
            }}
          >
            <MagnifierIcon size={16} />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search machines"
              aria-label="Search machines"
              style={{
                background: "transparent",
                border: "none",
                color: COLORS.primaryText,
                flexGrow: 1,
                fontFamily: FONT_SORA,
                fontSize: "14px",
                lineHeight: "20px",
                minWidth: 0,
                outline: "none",
                padding: 0,
              }}
            />
            <span
              style={{
                alignItems: "center",
                backgroundColor: COLORS.charcoal,
                borderRadius: "5px",
                display: "flex",
                flexShrink: 0,
                padding: "3px 7px",
              }}
            >
              <span
                style={{
                  color: COLORS.charcoalLight,
                  fontFamily: FONT_MONO,
                  fontSize: "9px",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                }}
              >
                {/Mac/i.test(navigator.platform) ? "⌘K" : "Ctrl K"}
              </span>
            </span>
          </div>

          {/* Sort header */}
          <div
            style={{
              alignItems: "center",
              backgroundColor: COLORS.inputBg,
              borderBottomColor: COLORS.rowDivider,
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              boxSizing: "border-box",
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              padding: "8px 18px",
            }}
          >
            <span
              style={{
                color: COLORS.dimText,
                fontFamily: FONT_MONO,
                fontSize: "9px",
                fontWeight: 500,
                letterSpacing: "0.12em",
                lineHeight: "12px",
                textTransform: "uppercase",
              }}
            >
              {machines == null
                ? "loading…"
                : `${filtered.length} ${filtered.length === 1 ? "machine" : "machines"}`}
            </span>
            <span style={{ alignItems: "center", display: "flex", gap: "6px" }}>
              <span
                style={{
                  color: COLORS.dimText,
                  fontFamily: FONT_MONO,
                  fontSize: "9px",
                  fontWeight: 500,
                  letterSpacing: "0.12em",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                }}
              >
                Sort
              </span>
              <span
                style={{
                  color: COLORS.primaryText,
                  fontFamily: FONT_MONO,
                  fontSize: "9px",
                  fontWeight: 500,
                  letterSpacing: "0.12em",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                }}
              >
                online · fresh · name
              </span>
              <SortIcon />
            </span>
          </div>

          {/* Rows */}
          <div
            role="listbox"
            aria-label="Available machines"
            style={{
              maxHeight: "360px",
              overflowY: "auto",
            }}
          >
            {filtered.length === 0 && machines != null && (
              <div
                style={{
                  color: COLORS.dimText,
                  fontFamily: FONT_INTER,
                  fontSize: "13px",
                  fontStyle: "italic",
                  lineHeight: "18px",
                  padding: "24px 18px",
                  textAlign: "center",
                }}
              >
                {search.trim() ? "No machines match" : "No connected machines"}
              </div>
            )}

            {filtered.map((m, idx) => {
              const isCurrent = m.machineId === currentMachineId;
              const isHighlighted = idx === highlightIdx;
              const meta = deriveRowMeta(m, now);
              const dotColor = dotColorForRow(meta.freshness);

              // Row background:
              //   current → sage tint always (with green left bar)
              //   highlighted (not current) → very subtle inputBg tint
              //   default → white with bottom divider
              const rowBg = isCurrent
                ? COLORS.greenBg
                : isHighlighted
                  ? COLORS.inputBg
                  : COLORS.white;
              const isLast = idx === filtered.length - 1;

              // Status chip — solid for current, outlined otherwise. Colours
              // by meta.freshness.
              let chipBg = COLORS.white;
              let chipBorder = COLORS.green;
              let chipText = COLORS.green;
              if (meta.freshness === "stale") {
                chipBg = COLORS.amberBgChip;
                chipBorder = COLORS.amberBorderChip;
                chipText = COLORS.amberText;
              } else if (meta.freshness === "offline") {
                chipBg = COLORS.greyChipBg;
                chipBorder = COLORS.greyChipBorder;
                chipText = COLORS.grey;
              }
              if (isCurrent) {
                chipBg = chipText; // solid
                chipText = COLORS.charcoalLight;
              }

              const rowNameWeight = isCurrent ? 600 : 500;
              const rowNameColor =
                meta.freshness === "offline" ? COLORS.bodyText : COLORS.primaryText;
              const rowGuidColor =
                meta.freshness === "offline" ? COLORS.veryDimRow : COLORS.bodyText;

              return (
                <div
                  key={m.machineId}
                  ref={(el) => {
                    rowsRef.current[idx] = el;
                  }}
                  role="option"
                  aria-selected={isCurrent}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onMouseDown={(e) => {
                    // mousedown so we beat the document listener that closes
                    // the picker on outside-click — clicks inside the listbox
                    // ARE inside the container, but using mousedown also
                    // sidesteps focus loss on the search input.
                    e.preventDefault();
                    selectMachine(m);
                  }}
                  style={{
                    alignItems: "center",
                    backgroundColor: rowBg,
                    borderBottomColor: COLORS.rowDivider,
                    borderBottomStyle: "solid",
                    borderBottomWidth: isLast ? 0 : "1px",
                    borderLeftColor: isCurrent ? COLORS.green : "transparent",
                    borderLeftStyle: "solid",
                    borderLeftWidth: "3px",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    display: "flex",
                    gap: "14px",
                    padding: isCurrent ? "13px 18px 13px 15px" : "13px 18px",
                  }}
                >
                  {/* Status dot — fixed 18px slot */}
                  <span
                    aria-hidden="true"
                    style={{
                      alignItems: "center",
                      display: "flex",
                      flexShrink: 0,
                      height: "18px",
                      justifyContent: "center",
                      width: "18px",
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: dotColor,
                        borderRadius: "50%",
                        boxShadow: isCurrent ? `0 0 0 3px ${COLORS.ringGreen}` : undefined,
                        flexShrink: 0,
                        height: "9px",
                        width: "9px",
                      }}
                    />
                  </span>

                  {/* Name + GUID — flex 1 */}
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      gap: "2px",
                      minWidth: 0,
                    }}
                  >
                    <span style={{ alignItems: "center", display: "flex", gap: "8px" }}>
                      <span
                        style={{
                          color: rowNameColor,
                          fontFamily: FONT_SORA,
                          fontSize: "14px",
                          fontWeight: rowNameWeight,
                          letterSpacing: "-0.005em",
                          lineHeight: "18px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.name}
                      </span>
                      {isCurrent && (
                        <span
                          style={{
                            alignItems: "center",
                            backgroundColor: COLORS.charcoal,
                            borderRadius: "3px",
                            color: COLORS.charcoalLight,
                            display: "flex",
                            flexShrink: 0,
                            fontFamily: FONT_MONO,
                            fontSize: "8px",
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            lineHeight: "10px",
                            padding: "1px 5px",
                            textTransform: "uppercase",
                          }}
                        >
                          Current
                        </span>
                      )}
                      {meta.freshness === "stale" && !isCurrent && (
                        <span
                          style={{
                            color: COLORS.amberText,
                            fontFamily: FONT_MONO,
                            fontSize: "9px",
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            lineHeight: "12px",
                            textTransform: "uppercase",
                          }}
                        >
                          stale
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        color: rowGuidColor,
                        fontFamily: FONT_MONO,
                        fontSize: "11px",
                        letterSpacing: "0.04em",
                        lineHeight: "14px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.machineId.slice(0, 13)}
                    </span>
                  </span>

                  {/* Time — fixed 74px slot, right-aligned */}
                  <span
                    style={{
                      color: meta.agoColor,
                      flexShrink: 0,
                      fontFamily: FONT_MONO,
                      fontSize: "11px",
                      letterSpacing: "0.02em",
                      lineHeight: "14px",
                      textAlign: "right",
                      width: "74px",
                    }}
                  >
                    {meta.agoLabel}
                  </span>

                  {/* Status chip — fixed 74px slot */}
                  <span
                    style={{
                      alignItems: "center",
                      backgroundColor: chipBg,
                      borderColor: chipBorder,
                      borderRadius: "10px",
                      borderStyle: "solid",
                      borderWidth: "1px",
                      boxSizing: "border-box",
                      display: "flex",
                      flexShrink: 0,
                      justifyContent: "center",
                      padding: isCurrent ? "3px 9px" : "2px 9px",
                      width: "74px",
                    }}
                  >
                    <span
                      style={{
                        color: chipText,
                        fontFamily: FONT_MONO,
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        lineHeight: "12px",
                        textTransform: "uppercase",
                      }}
                    >
                      {meta.statusLabel}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div
            style={{
              alignItems: "center",
              backgroundColor: COLORS.inputBg,
              borderTopColor: COLORS.border,
              borderTopStyle: "solid",
              borderTopWidth: "1px",
              boxSizing: "border-box",
              display: "flex",
              justifyContent: "space-between",
              padding: "9px 18px",
            }}
          >
            <span style={{ alignItems: "center", display: "flex", gap: "10px" }}>
              <KbdChip>{"↑↓"}</KbdChip>
              <span
                style={{
                  color: COLORS.dimText,
                  fontFamily: FONT_MONO,
                  fontSize: "9px",
                  letterSpacing: "0.08em",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                }}
              >
                navigate
              </span>
              <KbdChip>{"↵"}</KbdChip>
              <span
                style={{
                  color: COLORS.dimText,
                  fontFamily: FONT_MONO,
                  fontSize: "9px",
                  letterSpacing: "0.08em",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                }}
              >
                load
              </span>
            </span>
            <span
              style={{
                color: COLORS.veryDim,
                fontFamily: FONT_INTER,
                fontSize: "11px",
                fontStyle: "italic",
                lineHeight: "14px",
              }}
            >
              live · refreshes every 5s
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachinePicker;
